/**
 * Pill switching for the "En ce moment" band (sections/kurashi-collection-now.liquid).
 *
 * Every panel is rendered server-side and all but the first carry `hidden`, so the band shows
 * real products with no JS at all — this only adds the switching. That is also why visibility
 * is toggled with the `hidden` property rather than a class: base.css sets
 * [hidden] { display: none !important }, and a class-based toggle would have to fight it.
 *
 * Follows the WAI-ARIA tabs pattern: one tab in the roving tabindex, arrows to move between
 * them, Home/End to jump.
 */
(() => {
  const init = (root) => {
    if (root.dataset.nowReady === 'true') return;
    root.dataset.nowReady = 'true';

    const tablist = root.querySelector('[role="tablist"]');
    if (!tablist) return;
    const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
    if (tabs.length < 2) return;

    const panelFor = (tab) => root.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);

    /**
     * A `best` tab whose page was not already rendered in best-selling order carries an empty
     * rail and the URL to fetch. Shopify's Section Rendering API returns this same section
     * rendered with that sort applied, where the tab does render its cards server-side — so we
     * take the matching rail out of the response. Fetched once, on first activation.
     */
    const fill = (panel) => {
      const rail = panel.querySelector('[data-now-fetch]');
      if (!rail || rail.dataset.nowLoaded === 'true' || rail.dataset.nowLoading === 'true') return;
      rail.dataset.nowLoading = 'true';

      fetch(rail.dataset.nowFetch)
        .then((res) => (res.ok ? res.text() : Promise.reject(new Error(res.status))))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const source = doc.querySelector(
            `.kurashi-now__panel[id$="-panel-${rail.dataset.nowBlock}"] .kurashi-now__rail`
          );
          if (!source || !source.children.length) throw new Error('no rail in response');
          rail.innerHTML = source.innerHTML;
          rail.classList.remove('kurashi-now__rail--pending');
          rail.dataset.nowLoaded = 'true';
        })
        .catch(() => {
          // Leave the tab empty rather than showing the wrong order; the other tabs still work.
          panel.hidden = true;
          const tab = root.querySelector(`[aria-controls="${CSS.escape(panel.id)}"]`);
          if (tab) tab.hidden = true;
        })
        .finally(() => {
          delete rail.dataset.nowLoading;
        });
    };

    const select = (tab, { focus = false } = {}) => {
      tabs.forEach((t) => {
        const isCurrent = t === tab;
        t.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
        t.tabIndex = isCurrent ? 0 : -1;
        const panel = panelFor(t);
        if (panel) {
          panel.hidden = !isCurrent;
          if (isCurrent) fill(panel);
        }
      });
      if (focus) tab.focus();
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => select(tab));
    });

    // The tab that is open on load may itself be awaiting a fetch.
    const open = tabs.find((t) => t.getAttribute('aria-selected') === 'true');
    if (open) {
      const panel = panelFor(open);
      if (panel) fill(panel);
    }

    tablist.addEventListener('keydown', (event) => {
      const current = tabs.indexOf(document.activeElement);
      if (current === -1) return;

      let next = null;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = (current + 1) % tabs.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (current - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      select(tabs[next], { focus: true });
    });
  };

  const scan = (scope) => {
    (scope || document).querySelectorAll('[data-kurashi-now]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  // The theme editor re-renders a section in place; the fresh markup needs binding again.
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
